import * as React from "react";
import { useTranslation } from "react-i18next";
import { Collapse, makeStyles, Box } from "@material-ui/core";
import { ExpandLess, ExpandMore } from "@material-ui/icons";
import { useSelector } from "react-redux";
import { IAppReduxState } from "src/common/types";
import { Loader } from "src/common/components";
import MenuItem from "../MenuItem";
import { separateSubOrdinatesByLevels } from "./helpers";
import InsightsIcon from "@mui/icons-material/Insights";

const useStyles = makeStyles((theme: any) => ({
  menuLabel: {
    marginRight: "7px"
  },
  subMenuLabel: {
    whiteSpace: "initial"
  },
  subMenu: {
    margin: "0 0 0 45px"
  },
  subLevelOneMenu: {
    margin: "0 0 0 15px"
  }
}));
interface IProps {
  lng: string;
  subPage?: string;
  userId: string;
  currentPage: string;
  dataTestId: string;
}
const ManagerDashboardSideMenu = ({ lng, subPage, userId, currentPage, dataTestId }: IProps) => {
  const { t } = useTranslation();
  const {
    items: { reports, pending, rejected }
  } = useSelector((state: IAppReduxState) => state.managerDashboard);
  const [zerothLevel, firstLevels, secondLevels] = separateSubOrdinatesByLevels(reports);
  const shouldFirstCollapseOpen = currentPage === "manager-dashboard";

  const [openZerothMenu, setOpenZerothMenu] = React.useState(shouldFirstCollapseOpen);
  const [openFirstMenu, setOpenFirstMenu] = React.useState(true);
  const [openSecondMenu, setOpenSecondMenu] = React.useState(true);
  const classes = useStyles();
  return (
    <div>
      <MenuItem
        selected={subPage === userId}
        to={`/${lng}/dashboard/manager-dashboard/${userId}`}
        dataTestId={dataTestId}
        onClick={() => {
          if (!openZerothMenu) setOpenZerothMenu(!openZerothMenu);
        }}
      >
        <InsightsIcon />
        <span className={classes.menuLabel}>{t("feedback.sidemenu.manager-dashboard")}</span>
        <Box
          display="flex"
          onClick={e => {
            e.preventDefault();
            setOpenZerothMenu(!openZerothMenu);
          }}
        >
          {!pending && (openZerothMenu ? <ExpandLess /> : <ExpandMore />)}
        </Box>
      </MenuItem>

      {!rejected && (
        <Collapse in={openZerothMenu} timeout="auto" unmountOnExit>
          {pending ? (
            <Loader />
          ) : (
            <div className={classes.subMenu}>
              {/* If current user is level 0, dont show him/her and start tree from level 1, put all level 2's under selected level 1
               */}
              {zerothLevel?.id === userId ? (
                <div>
                  <div className={classes.subLevelOneMenu}>
                    {firstLevels.map(firstLevel => {
                      const { subordinatesCount, name, id } = firstLevel;
                      const isUserSelected = subPage === id;
                      return (
                        <div key={id}>
                          <MenuItem
                            selected={isUserSelected}
                            to={`/${lng}/dashboard/manager-dashboard/${id}`}
                            disabled={subordinatesCount === 0}
                            dataTestId={`${dataTestId}-${name}`}
                            name={name}
                            onClick={() => {
                              if (!openSecondMenu) setOpenSecondMenu(!openSecondMenu);
                            }}
                          >
                            <span className={classes.subMenuLabel}>
                              {name} {subordinatesCount > 0 && `(${subordinatesCount})`}
                            </span>
                            <Box
                              display="flex"
                              onClick={e => {
                                e.preventDefault();
                                setOpenSecondMenu(!openSecondMenu);
                              }}
                            >
                              {/* if user selected and has subordinates */}
                              {isUserSelected &&
                                subordinatesCount > 0 &&
                                (openSecondMenu ? <ExpandLess /> : <ExpandMore />)}
                            </Box>
                          </MenuItem>
                          {/* put collabsible menu under selected level */}
                          {isUserSelected && (
                            <Collapse in={openSecondMenu} timeout="auto" unmountOnExit>
                              <div className={classes.subLevelOneMenu}>
                                {secondLevels.map(secondLevel => {
                                  const {
                                    id: secondLevelId,
                                    subordinatesCount: secondLevelSubordinates,
                                    name: secondLevelName
                                  } = secondLevel;
                                  const hasUserSelected = subPage === secondLevelId;
                                  return (
                                    <MenuItem
                                      key={secondLevelId}
                                      selected={hasUserSelected}
                                      to={`/${lng}/dashboard/manager-dashboard/${secondLevelId}`}
                                      disabled={secondLevelSubordinates === 0}
                                      dataTestId={`${dataTestId}-${secondLevelName}`}
                                      name={secondLevelName}
                                    >
                                      <span className={classes.subMenuLabel}>
                                        {secondLevelName}{" "}
                                        {secondLevelSubordinates > 0 && `(${secondLevelSubordinates})`}
                                      </span>
                                    </MenuItem>
                                  );
                                })}
                              </div>
                            </Collapse>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                // If current user is not level 0 start tree from level 0 and fill it all level 2's under selected level 1

                <div>
                  <MenuItem
                    selected={subPage === zerothLevel?.id}
                    to={`/${lng}/dashboard/manager-dashboard/${zerothLevel?.id}`}
                    disabled={zerothLevel?.subordinatesCount === 0}
                    dataTestId={`${dataTestId}-${zerothLevel?.name}`}
                    name={zerothLevel?.name}
                  >
                    <span className={classes.subMenuLabel}>
                      {zerothLevel?.name} {zerothLevel?.subordinatesCount > 0 && `(${zerothLevel?.subordinatesCount})`}{" "}
                    </span>
                    <Box
                      display="flex"
                      onClick={e => {
                        e.preventDefault();
                        setOpenFirstMenu(!openFirstMenu);
                      }}
                    >
                      {zerothLevel?.subordinatesCount > 0 && openFirstMenu ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  </MenuItem>
                  <Collapse in={openFirstMenu} timeout="auto" unmountOnExit>
                    <div className={classes.subLevelOneMenu}>
                      {firstLevels?.map(firstLevel => (
                        <div key={firstLevel.id}>
                          <MenuItem
                            selected={subPage === firstLevel.id}
                            to={`/${lng}/dashboard/manager-dashboard/${firstLevel.id}`}
                            disabled={firstLevel.subordinatesCount === 0}
                            dataTestId={`${dataTestId}-${firstLevel?.name}`}
                            name={firstLevel.name}
                          >
                            <span className={classes.subMenuLabel}>
                              {firstLevel.name}{" "}
                              {firstLevel.subordinatesCount > 0 && `(${firstLevel.subordinatesCount})`}
                            </span>
                            <Box
                              display="flex"
                              onClick={e => {
                                e.preventDefault();
                                setOpenSecondMenu(!openSecondMenu);
                              }}
                            >
                              {subPage === firstLevel.id &&
                                firstLevel.subordinatesCount > 0 &&
                                (openSecondMenu ? <ExpandLess /> : <ExpandMore />)}
                            </Box>
                          </MenuItem>
                          {subPage === firstLevel.id && (
                            <Collapse in={openSecondMenu} timeout="auto" unmountOnExit>
                              <div className={classes.subLevelOneMenu}>
                                {secondLevels.map(secondLevel => (
                                  <MenuItem
                                    key={secondLevel.id}
                                    selected={subPage === secondLevel.id}
                                    to={`/${lng}/dashboard/manager-dashboard/${secondLevel.id}`}
                                    disabled={secondLevel.subordinatesCount === 0}
                                    dataTestId={`${dataTestId}-${secondLevel?.name}`}
                                    name={secondLevel.name}
                                  >
                                    <span className={classes.subMenuLabel}>
                                      {secondLevel.name}{" "}
                                      {secondLevel.subordinatesCount > 0 && `(${secondLevel.subordinatesCount})`}
                                    </span>
                                  </MenuItem>
                                ))}
                              </div>
                            </Collapse>
                          )}
                        </div>
                      ))}
                    </div>
                  </Collapse>
                </div>
              )}
            </div>
          )}
        </Collapse>
      )}
    </div>
  );
};

export default ManagerDashboardSideMenu;
